import { useEffect, useMemo, useState } from "react";
import ActivityModule from "./activity.tsx";
import GraphModule from "./graph.tsx";
import KpisModule from "./kpis.tsx";
import KursModule from "./kurs.tsx";
import TransactionsModule from "./transactions.tsx";
import { useSelector } from "react-redux";
import { RootState } from "../../../state/store.ts";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import CallsModule from "./calls.tsx";
import LatestCallsModule from "./latestcalls.tsx";
import CustomerDetailModule from "./customerdetail.tsx";

type DashboardModuleProps = {
    enlargedCard: any,
    setEnlargedCard: (obj: any) => void,
    baseUrl: string,
    currentUser: any,
    setTab: (tab: string) => void,
    setSelectedAgent: (agent: any) => void,
    tab: string,
}
export default function DashboardModule({ enlargedCard, setEnlargedCard, baseUrl, currentUser, setTab, setSelectedAgent, tab }: DashboardModuleProps) {
    const [duration, setDuration] = useState("30");
    const [filter, setFilter] = useState("today");
    const [kpisLoading, setKpisLoading] = useState(false);

    const [kpis, setKpis] = useState([
        { label: "Omsetning denne måneden", value: "", trend: "" },
        { label: "Aktive agenter", value: "18", trend: "+2" },
        { label: "Aktive kunder", value: "642", trend: "+37" },
        { label: "Calls denne måneden", value: "312", trend: "+9%" },
    ]);
    const client = useSelector((state: RootState) => state.client.client);

    const getKpis = async () => {
        try {
            setKpisLoading(true);
            const response = await axios.get(baseUrl + '/getClientKpis', { params: { token: currentUser?.token, client_uid: client?.uid, filter: filter } });

            if (response.data.status === "OK") {
                setKpis(response.data.data);
            }
        } catch (err) {
            console.error(err)
        } finally {
            setKpisLoading(false);
        }
    }


    useEffect(() => {
        getKpis();
    }, [filter, client])

    const fetchAgents = async () => {
        const response = await axios.get(baseUrl + "/getAgents", {
            params: { client_uid: client?.uid, filter }
        });
        return response.data.data;
    };

    const { data: recentAgents, isFetching: agentsLoading } = useQuery({
        queryKey: ["agents", client?.uid, filter],
        queryFn: fetchAgents,
        staleTime: Infinity,
        enabled: !!client,
    });

    const fetchStatuses = async () => {
        const response = await axios.get(baseUrl + "/getAgentsStatuses", {
            params: { client_uid: client?.uid }
        });
        return response.data.data; // [{ uid, call_status }]
    };

    const { data: statuses } = useQuery({
        queryKey: ["agent-statuses", client?.uid],
        queryFn: fetchStatuses,
        refetchInterval: 4000, // poll every 4s
        enabled: !!client,
    });

    const mergedAgents = useMemo(() => {
        if (!recentAgents || !statuses) return recentAgents || [];
        return recentAgents.map(agent => {
            const match = statuses.find(s => s.uid === agent.uid);
            return {
                ...agent,
                call_status: match?.call_status || "offline"
            };
        });
    }, [recentAgents, statuses]);
    const [latestCallsSeeAll, setLatestCallsSeeAll] = useState(false);

    const [selectedId, setSelectedId] = useState<string | null>(null);

    const [selectedCustomer, setSelectedCustomer] = useState(null);

    if (selectedCustomer && selectedId) {
        return (
            <CustomerDetailModule tab={tab} setTab={setTab} customer={selectedCustomer} setSelectedId={setSelectedId} baseUrl={baseUrl} currentUser={currentUser} client={client} />
        )
    }


    if (latestCallsSeeAll) {
        return (
            <>
                <button
                    className="mb-6 text-slate-500 hover:text-slate-900 text-sm"
                    onClick={() => setLatestCallsSeeAll(false)}
                >
                    ← Tilbake til Dashboard
                </button>

                <div className="my-3">
                    <CallsModule baseUrl={baseUrl} client={client} currentUser={currentUser} tab={tab} setTab={setTab} setSelectedAgent={setSelectedAgent} />
                </div>
            </>
        )
    }

    return (
        <>
            <div className="flex gap-2 ml-auto justify-self-end text-sm">
                <button
                    onClick={() => setFilter('today')}
                    className={`rounded-full px-4 py-2 border ${filter === "today" ? "bg-purple-600 text-white border-purple-600" : ""
                        }`}
                >
                    Today
                </button>

                <button
                    onClick={() => setFilter("week")}
                    className={`rounded-full px-4 py-2 border ${filter === "week" ? "bg-purple-600 text-white border-purple-600" : ""
                        }`}
                >
                    Week
                </button>

                <button
                    onClick={() => setFilter("month")}
                    className={`rounded-full px-4 py-2 border ${filter === "month" ? "bg-purple-600 text-white border-purple-600" : ""
                        }`}
                >
                    Month
                </button>
                <button
                    onClick={() => setFilter("year")}
                    className={`rounded-full px-4 py-2 border ${filter === "year" ? "bg-purple-600 text-white border-purple-600" : ""
                        }`}
                >
                    Year
                </button>
            </div>
            {/* KPI ROW */}
            <KpisModule enlargedCard={enlargedCard} setEnlargedCard={setEnlargedCard} baseUrl={baseUrl} kpis={kpis} kpisLoading={kpisLoading} filter={filter} currentUser={currentUser} />

            {/* GRAPH + ACTIVITY */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                {/* <GraphModule duration={duration} setDuration={setDuration} /> */}
                {/* TRANSAKSJONER */}
                <LatestCallsModule baseUrl={baseUrl} client={client} currentUser={currentUser} setLatestCallsSeeAll={setLatestCallsSeeAll} setSelectedId={setSelectedId} setSelectedCustomer={setSelectedCustomer} />

                {/* ACTIVITY */}
                <ActivityModule filter={filter} currentUser={currentUser} client={client} recentAgents={mergedAgents ?? []} agentsLoading={agentsLoading} setTab={setTab} baseUrl={baseUrl} setSelectedAgent={setSelectedAgent} />
            </section>
            {/* BOTTOM SECTION – KURS & TRANSAKSJONER */}
            <section className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start mt-4">
                {/* KURS */}
                <KursModule baseUrl={baseUrl} client={client} currentUser={currentUser} setTab={setTab} />

                {/* TRANSAKSJONER */}
                <TransactionsModule baseUrl={baseUrl} client={client} currentUser={currentUser} setSelectedId={setSelectedId} setSelectedCustomer={setSelectedCustomer} />
            </section>
        </>
    )
}